import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { storeAPI } from '../../services/api';
import { Package, ShoppingCart, ArrowLeft, CheckCircle } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const PACK_TYPES = { NEC: 'Primera Necesidad', BEB: 'Bebé', LIC: 'Licor', MAR: 'Marca Sttora', SNK: 'Snacks', MASC: 'Mascotas', CON: 'Condimentos', ASEO_DAMA: 'Aseo Dama', ASEO_CAB: 'Aseo Caballero', ASEO_NIN: 'Aseo Niños', VEG: 'Vegano', GFREE: 'Gluten Free', LAV: 'Lavandería', COC: 'Cocina' };

const StorePackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addPack } = useCart();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    storeAPI.getPack(id)
      .then(res => setPack(res.data))
      .catch(() => navigate('/store/packs'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleAddToCart = async () => {
    try {
      await addPack(parseInt(id));
    } catch { navigate('/login'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full" />
      </div>
    );
  }

  if (!pack) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-4 py-8">
      <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/store/packs')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-orange-600 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Packs
      </motion.button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        <div className="p-8">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-xl shrink-0">
              <Package className="w-16 h-16 text-white" />
            </div>
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{pack.name}</h1>
                <span className={`px-3 py-1 rounded-lg text-xs font-medium ${pack.is_active ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-500'}`}>
                  {PACK_TYPES[pack.pack_type] || pack.pack_type}
                </span>
              </div>
              <p className="text-sm text-gray-400 font-mono mb-4">{pack.sku}</p>
              {pack.description && (
                <p className="text-gray-600 dark:text-gray-300 mb-6">{pack.description}</p>
              )}
              <div className="flex items-end gap-6 flex-wrap">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Precio del Pack</p>
                  <span className="text-4xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                    ${pack.price?.toFixed(2)}
                  </span>
                </div>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={handleAddToCart}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30 hover:shadow-xl transition-all"
                >
                  <ShoppingCart className="w-5 h-5" /> Agregar al Carrito
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-700 p-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Productos Incluidos</h2>
          <div className="space-y-4">
            {(pack.items || []).map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl"
              >
                <CheckCircle className="w-6 h-6 text-emerald-500 shrink-0" />
                <div className="flex-1">
                  <Link to={`/store/products/${item.product_id}`} className="font-semibold text-gray-900 dark:text-white hover:text-orange-500">
                    {item.product_name || `Producto #${item.product_id}`}
                  </Link>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Cantidad: {item.quantity}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StorePackDetail;
